import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'src/hooks/useAuth';
import { OpportunityService } from '../OpportunityService';
import { JobService } from '../JobService';

// Custom hook to handle role-based queries
const useRoleBasedQueries = () => {
  const { getUserInfo } = useAuth();
  const user = getUserInfo();
  const isCse = user?.roles?.includes('ahedge_cse');
  const isTa = user?.roles?.includes('ahedge_ta');

  return { isCse, isTa };
};

// Get opportunity details — same requirement-details endpoint for every
// role now (CSE and TA alike), keyed by reqId. `enabled` lets a caller defer
// the fetch (e.g. InterviewBrief.jsx's Job description tab only fetches once
// actually opened) — every existing call site omits it and keeps fetching
// immediately, per its default.
export const useOpportunityDetails = (
  reqId,
  { requirementLocationId, assignmentId, enabled = true } = {}
) =>
  useQuery({
    queryKey: [
      'opportunity',
      'details',
      reqId,
      requirementLocationId,
      assignmentId,
    ],
    queryFn: () => OpportunityService.getOpportunityDetails({ reqId }),
    enabled: !!reqId && enabled,
  });

// Get requirement locations (location-wise headcount) - role aware
export const useRequirementLocations = (
  reqId,
  { enabled = true, assignmentId } = {}
) => {
  const { isTa: isTaRole } = useRoleBasedQueries();

  return useQuery({
    queryKey: [
      'opportunity',
      'requirement-locations',
      reqId,
      assignmentId,
      isTaRole ? 'ta' : 'cse',
    ],
    queryFn: () =>
      isTaRole && assignmentId
        ? OpportunityService.getAssignmentLocations(assignmentId)
        : OpportunityService.getRequirementLocations(reqId),
    enabled: isTaRole ? !!assignmentId && enabled : !!reqId && enabled,
  });
};

// Get opportunities for CSE
export const useOpportunitiesCse = ({
  status,
  page,
  pageSize,
  activeTab,
  search,
}) => {
  const { isCse } = useRoleBasedQueries();
  return useQuery({
    queryKey: [
      'opportunities',
      'cse',
      status,
      page,
      pageSize,
      activeTab,
      search,
    ],
    queryFn: () =>
      OpportunityService.getOpportunitiesCse({
        status,
        page,
        pageSize,
        activeTab,
        search,
      }),
    enabled: isCse,
    staleTime: 0, // Data is immediately stale - always refetch
    gcTime: 0, // Don't cache - remove from cache immediately after unmount
    refetchOnMount: true, // Always refetch on mount
    // `status` is part of the queryKey and changes on every tab click
    // (New/Active/Archive) — without this, that's a brand-new queryKey with
    // no cached data yet, so `data` (and therefore the tab bar's own count
    // badges, which read off this same response's `metrics`, not just the
    // card list below it) goes undefined for a beat on every single switch.
    // Keeping the previous tab's data on screen while the new one loads in
    // the background is what actually fixes the flicker — confirmed live
    // this was the cause, not a CSS/animation issue on the tab strip itself.
    placeholderData: (prev) => prev,
  });
};

// Get the flows a CSE can pick between when opening a requirement for direct
// TA sourcing (radio group on the "Forward to TA" modal).
export const useAvailableFlows = (enabled = true) => {
  const { isCse } = useRoleBasedQueries();
  return useQuery({
    queryKey: ['opportunities', 'available-flows'],
    queryFn: () => OpportunityService.getAvailableFlows(),
    enabled: isCse && enabled,
  });
};

// Get opportunities for TA
export const useOpportunitiesTa = ({
  page,
  pageSize,
  activeTab,
  statusFilters,
  isMock,
  isArchive,
  search,
}) => {
  const { isTa } = useRoleBasedQueries();
  return useQuery({
    queryKey: [
      'opportunities',
      'ta',
      page,
      pageSize,
      activeTab,
      statusFilters,
      isMock,
      isArchive,
      search,
    ],
    queryFn: () =>
      OpportunityService.getOpportunitiesTa({
        page,
        pageSize,
        activeTab,
        statusFilters,
        isMock,
        isArchive,
        search,
      }),
    enabled: isTa,
    staleTime: 0, // Data is immediately stale - always refetch
    gcTime: 0, // Don't cache - remove from cache immediately after unmount
    refetchOnMount: true, // Always refetch on mount
  });
};

// Get opportunity history/audit trail
export const useOpportunityHistory = (id) => {
  const { isCse } = useRoleBasedQueries();
  return useQuery({
    queryKey: ['opportunity', 'history', id],
    queryFn: () => OpportunityService.getHistory(id),
    enabled: !!id && isCse,
  });
};

export const useAssignmentPartners = (assignmentId, enabled = true) =>
  useQuery({
    queryKey: ['assignment-partners', assignmentId],
    queryFn: () => OpportunityService.getAssignmentPartners(assignmentId),
    enabled: !!assignmentId && enabled,
  });

export const useAssignmentInstitutions = (assignmentId, enabled = true) =>
  useQuery({
    queryKey: ['assignment-institutions', assignmentId],
    queryFn: () => OpportunityService.getAssignmentInstitutions(assignmentId),
    enabled: !!assignmentId && enabled,
  });

// The flow's state machine for this assignment — `data.statuses` drives the
// pipeline's filter tabs, since the set of statuses (and which are final,
// which are sub-states of another) varies flow to flow.
export const useFlowStatuses = (assignmentId, enabled = true) =>
  useQuery({
    queryKey: ['assignment', 'flow-statuses', assignmentId],
    queryFn: () => OpportunityService.getFlowStatuses(assignmentId),
    enabled: !!assignmentId && enabled,
  });

// Every configured flow (Entry Level/Senior Level/...) — powers the
// Dashboard's Assignment Status flow picker. Distinct from useAvailableFlows
// above, which is the requirement-scoped "Forward to TA" modal's picker.
export const useFlows = (enabled = true) =>
  useQuery({
    queryKey: ['flows', 'list'],
    queryFn: () => OpportunityService.getFlows(),
    enabled,
  });

// Same resource as useFlowStatuses above, addressed by state_machine_key
// directly instead of through an assignment — for the Dashboard's flow
// picker, which has no real assignment in hand.
export const useFlowStatusesByKey = (stateMachineKey, enabled = true) =>
  useQuery({
    queryKey: ['flows', 'flow-statuses', stateMachineKey],
    queryFn: () => OpportunityService.getFlowStatusesByKey(stateMachineKey),
    enabled: !!stateMachineKey && enabled,
  });

export const useJobInstitutions = (jobCode, enabled = true) =>
  useQuery({
    queryKey: ['job-institutions', jobCode],
    queryFn: () => JobService.getJobInstitutions(jobCode),
    enabled: !!jobCode && enabled,
  });

// Get CSE dashboard metrics — period: '7d' | '30d' | '90d' | '1y', matching
// the Dashboard page's own period selector (see Dashboard.jsx's PERIODS).
// `enabled` lets the Dashboard's own Real/Mocked toggle skip the network
// call entirely while showing mock data, rather than firing a request whose
// result just gets ignored.
export const useCseDashboardMetrics = (period = '30d', enabled = true) => {
  const { isCse } = useRoleBasedQueries();
  return useQuery({
    queryKey: ['cse', 'dashboard', 'metrics', period],
    queryFn: () => OpportunityService.getCseDashboardMetrics({ period }),
    enabled: isCse && enabled,
  });
};

// Get CSE high priority requirements
export const useCseHighPriorityRequirements = (enabled = true) => {
  const { isCse } = useRoleBasedQueries();
  return useQuery({
    queryKey: ['cse', 'dashboard', 'high-priority-requirements'],
    queryFn: () => OpportunityService.getCseHighPriorityRequirements(),
    enabled: isCse && enabled,
  });
};

// Assignment-level counterpart of useCseDashboardMetrics — the Dashboard's
// Assignment Overview section's real data source.
export const useAssignmentDashboardMetrics = (
  period = '30d',
  enabled = true
) => {
  const { isCse } = useRoleBasedQueries();
  return useQuery({
    queryKey: ['cse', 'dashboard', 'assignment-metrics', period],
    queryFn: () => OpportunityService.getAssignmentDashboardMetrics({ period }),
    enabled: isCse && enabled,
  });
};

// Assignment-level counterpart of useCseHighPriorityRequirements.
export const useAssignmentHighPriority = (enabled = true) => {
  const { isCse } = useRoleBasedQueries();
  return useQuery({
    queryKey: ['cse', 'dashboard', 'assignment-high-priority'],
    queryFn: () => OpportunityService.getAssignmentHighPriority(),
    enabled: isCse && enabled,
  });
};

// Team-scoped variants — not consumed by any UI yet (see
// OpportunityService.getAssignmentTeamDashboardMetrics' own comment).
export const useAssignmentTeamDashboardMetrics = (
  period = '30d',
  enabled = true
) => {
  const { isCse } = useRoleBasedQueries();
  return useQuery({
    queryKey: ['cse', 'dashboard', 'assignment-team-metrics', period],
    queryFn: () =>
      OpportunityService.getAssignmentTeamDashboardMetrics({ period }),
    enabled: isCse && enabled,
  });
};

export const useAssignmentTeamHighPriority = (enabled = true) => {
  const { isCse } = useRoleBasedQueries();
  return useQuery({
    queryKey: ['cse', 'dashboard', 'assignment-team-high-priority'],
    queryFn: () => OpportunityService.getAssignmentTeamHighPriority(),
    enabled: isCse && enabled,
  });
};

// Get TA summary
export const useTaSummary = () => {
  const { isTa } = useRoleBasedQueries();
  return useQuery({
    queryKey: ['ta', 'summary'],
    queryFn: () => OpportunityService.getTaSummary(),
    enabled: isTa,
  });
};

// Get CSE requirements summary (the Opportunities-list summary strip —
// separate from the CSE dashboard metrics)
export const useCseRequirementsSummary = () => {
  const { isCse } = useRoleBasedQueries();
  return useQuery({
    queryKey: ['cse', 'requirements', 'summary'],
    queryFn: () => OpportunityService.getCseRequirementsSummary(),
    enabled: isCse,
  });
};
